import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, FileWarning, Plus, Search, Upload } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  CATALOG_COVERAGE_LABELS,
  type CatalogCoverage,
  type CatalogRow,
} from "../../../../../../shared/index";

/**
 * The Catalog page — every Endpoint as `FeatureUrl` × Products × docs entry.
 *
 * The legacy setup kept these two halves apart: the SQL catalog decided what could be
 * granted, a WordPress feed decided what was documented, and nothing compared them. So
 * the two states that cost money were invisible — an Endpoint customers can be granted
 * but cannot discover (`No docs`), and a documented Endpoint with no permission behind
 * it, published on the docs site and grantable to nobody (`No FeatureUrl`). Both are
 * filters here, and both are counters at the top.
 */

const COVERAGE_ORDER: CatalogCoverage[] = [
  "documented",
  "draft",
  "deprecated",
  "undocumented",
  "orphanDoc",
];

const COVERAGE_TONE: Record<CatalogCoverage, string> = {
  documented: "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200",
  draft: "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200",
  deprecated: "bg-muted text-muted-foreground",
  undocumented: "bg-orange-100 text-orange-900 dark:bg-orange-950 dark:text-orange-200",
  orphanDoc: "bg-red-100 text-red-900 dark:bg-red-950 dark:text-red-200",
};

type GroupBy = "product" | "category" | "none";

export default function B2bCatalog() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get("q") ?? "");
  const [openSlug, setOpenSlug] = useState<string | null>(null);

  const view = useQuery({ queryKey: ["b2b", "catalog", "view"], queryFn: api.b2b.catalogView });

  const coverage = params.get("coverage") as CatalogCoverage | null;
  const product = params.get("product");
  const groupBy = (params.get("groupBy") as GroupBy | null) ?? "none";
  const search = (params.get("q") ?? "").trim().toLowerCase();

  const rows = useMemo(() => {
    let list = view.data?.rows ?? [];
    if (coverage) list = list.filter((r) => r.coverage === coverage);
    if (product) list = list.filter((r) => r.products.includes(product));
    if (search) {
      list = list.filter(
        (r) =>
          r.path.toLowerCase().includes(search) ||
          r.doc?.title.toLowerCase().includes(search) ||
          r.doc?.slug.toLowerCase().includes(search)
      );
    }
    return list;
  }, [view.data, coverage, product, search]);

  /**
   * Grouping by Product repeats a row under each of its Products — an Endpoint really
   * does belong to several, and hiding that would misrepresent what a Product grants.
   */
  const groups = useMemo(() => {
    if (groupBy === "none") return [{ key: "", label: "", rows }] as const;
    const byKey = new Map<string, CatalogRow[]>();
    for (const row of rows) {
      const keys =
        groupBy === "product"
          ? row.products.length > 0
            ? row.products
            : ["— no product —"]
          : [row.doc?.categoryName ?? "— no docs —"];
      for (const key of keys) {
        const list = byKey.get(key) ?? [];
        list.push(row);
        byKey.set(key, list);
      }
    }
    return [...byKey.entries()]
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([key, groupRows]) => ({ key, label: key, rows: groupRows }));
  }, [rows, groupBy]);

  function setParam(key: string, value: string | null) {
    const next = new URLSearchParams(params);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    setParams(next);
  }

  const counters = view.data?.counters;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Catalog &amp; docs</h1>
          <p className="text-sm text-muted-foreground">
            {counters
              ? `${counters.endpoints} endpoints · ${counters.guides} guide pages`
              : "Loading…"}
          </p>
        </div>
        <div className="flex gap-2">
          <PublishButton />
          <Button asChild>
            <Link to="/dashboard/b2b/catalog/new">
              <Plus className="mr-2 h-4 w-4" />
              New endpoint
            </Link>
          </Button>
        </div>
      </div>

      {view.data && !view.data.docsAvailable && (
        <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            The docs collection could not be read, so every endpoint below shows its raw path
            and no coverage. This is not the same as &ldquo;nothing is documented&rdquo;.
          </span>
        </div>
      )}

      {counters && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {COVERAGE_ORDER.map((key) => {
            const value =
              key === "orphanDoc" ? counters.orphanDocs : counters[key as keyof typeof counters];
            const active = coverage === key;
            return (
              <button
                key={key}
                type="button"
                onClick={() => setParam("coverage", active ? null : key)}
                className={`rounded-lg border p-4 text-left transition ${
                  active ? "border-primary ring-1 ring-primary" : "hover:bg-muted/50"
                }`}
              >
                <div className="text-2xl font-bold">{value as number}</div>
                <div className="text-sm text-muted-foreground">
                  {CATALOG_COVERAGE_LABELS[key]}
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <form
          className="flex items-center gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            setParam("q", query);
          }}
        >
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="w-64 pl-8"
              placeholder="Search path, title or slug…"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
          <Button type="submit" variant="secondary">
            Search
          </Button>
        </form>

        <select
          className="h-9 rounded-md border bg-background px-2 text-sm"
          value={groupBy}
          onChange={(event) => setParam("groupBy", event.target.value)}
        >
          <option value="none">No grouping</option>
          <option value="product">Group by Product</option>
          <option value="category">Group by Category</option>
        </select>

        <select
          className="h-9 rounded-md border bg-background px-2 text-sm"
          value={product ?? ""}
          onChange={(event) => setParam("product", event.target.value)}
        >
          <option value="">All products</option>
          {view.data?.products.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>

        {(coverage || product || search) && (
          <Button variant="ghost" onClick={() => setParams(new URLSearchParams())}>
            Clear filters
          </Button>
        )}

        <span className="ml-auto text-sm text-muted-foreground">
          {view.data ? `${rows.length} shown` : ""}
        </span>
      </div>

      {view.isError && (
        <p className="text-sm text-destructive">Could not load the catalog.</p>
      )}

      {groups.map((group) => (
        <div key={group.key} className="space-y-2">
          {group.label && <h2 className="text-sm font-semibold">{group.label}</h2>}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[90px]">Method</TableHead>
                  <TableHead>Endpoint</TableHead>
                  <TableHead>Products</TableHead>
                  <TableHead className="w-[150px]">Coverage</TableHead>
                  <TableHead className="w-[130px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {group.rows.map((row) => (
                  <CatalogTableRow
                    key={`${group.key}:${row.featureUrlId ?? row.doc?.slug}`}
                    row={row}
                    onOpenDoc={setOpenSlug}
                  />
                ))}
                {group.rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} className="py-8 text-center text-muted-foreground">
                      {view.isLoading ? "Loading…" : "No endpoints match these filters."}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      ))}

      <DocPreview slug={openSlug} onClose={() => setOpenSlug(null)} />
    </div>
  );
}

/**
 * Read-only preview of the stored document.
 *
 * Editing is TASK-0386; until then this is the honest affordance — an operator can see
 * exactly what is published for an Endpoint without a button that pretends to save.
 */
/**
 * Publish, with what it would do stated before it is pressed.
 *
 * The docs site is a function of one file, so publishing is the moment edits become
 * public — and the two numbers that matter are how many documents changed since the live
 * file was generated, and how many are being **held back** as drafts or hidden. A button
 * that only says "Publish" lets an editor ship half a site without noticing.
 */
function PublishButton() {
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: ["b2b", "catalog", "publish-status"],
    queryFn: api.b2b.publishStatus,
  });

  const publish = useMutation({
    mutationFn: api.b2b.publish,
    onSuccess: async (written) => {
      await queryClient.invalidateQueries({ queryKey: ["b2b", "catalog"] });
      const { result } = written;
      if (!written.applied) {
        toast.success(
          `Dry run — would publish ${result.counts.endpoints} endpoints and ` +
            `${result.counts.guides} guides as v${result.version}. Nothing was uploaded.`
        );
        return;
      }
      toast.success(
        `Published v${result.version}: ${result.counts.endpoints} endpoints, ` +
          `${result.counts.guides} guides, ${result.counts.redirects} redirects.` +
          (result.purge && !result.purge.ok
            ? " The CDN purge failed — the file is live but the edge may serve the old one for up to a minute."
            : "")
      );
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const changed = status.data?.changedSinceLastPublish ?? 0;
  const heldBack = (status.data?.heldBack.drafts ?? 0) + (status.data?.heldBack.hidden ?? 0);

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        variant="secondary"
        onClick={() => publish.mutate()}
        disabled={publish.isPending || status.isLoading}
      >
        <Upload className="mr-2 h-4 w-4" />
        {publish.isPending ? "Publishing…" : "Publish"}
        {changed > 0 && (
          <Badge variant="outline" className="ml-2">
            {changed} changed
          </Badge>
        )}
      </Button>
      {status.data && (
        <p className="text-right text-xs text-muted-foreground">
          {status.data.generatedAt
            ? `Live: v${status.data.version}, ${new Date(status.data.generatedAt).toLocaleString()}`
            : "Never published"}
          {heldBack > 0 && ` · ${heldBack} held back`}
        </p>
      )}
    </div>
  );
}

function DocPreview({ slug, onClose }: { slug: string | null; onClose: () => void }) {
  const doc = useQuery({
    queryKey: ["b2b", "catalog", "doc", slug],
    queryFn: () => api.b2b.endpointDoc(slug!),
    enabled: slug !== null,
  });

  return (
    <Dialog open={slug !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{doc.data?.title ?? slug}</DialogTitle>
          <DialogDescription className="font-mono text-xs">
            {doc.data ? `${doc.data.method} ${doc.data.path || "(guide)"}` : "Loading…"}
          </DialogDescription>
        </DialogHeader>

        {doc.isError && <p className="text-sm text-destructive">Could not load this document.</p>}

        {doc.data && (
          <div className="space-y-4 text-sm">
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">{doc.data.status}</Badge>
              <Badge variant="outline">{doc.data.category.name}</Badge>
              {!doc.data.visibleOnDocs && <Badge variant="outline">hidden</Badge>}
            </div>

            <p className="whitespace-pre-wrap text-muted-foreground">{doc.data.body}</p>

            <ParamTable title="Path params" params={doc.data.pathParams} />
            <ParamTable title="Query params" params={doc.data.queryParams} />

            {doc.data.response.fields.length > 0 && (
              <div>
                <h3 className="mb-1 font-semibold">Response fields</h3>
                <ul className="space-y-0.5">
                  {doc.data.response.fields.map((field) => (
                    <li key={field.name} className="text-xs">
                      <span className="font-mono">{field.name}</span>{" "}
                      <span className="text-muted-foreground">
                        ({field.type}) {field.description}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Last edited {new Date(doc.data.updatedAt).toLocaleString()} by {doc.data.updatedBy}
                {" · "}version {doc.data.version}
              </p>
              <Button size="sm" asChild>
                <Link to={`/dashboard/b2b/catalog/${slug}`}>Edit</Link>
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ParamTable({
  title,
  params,
}: {
  title: string;
  params: { name: string; type: string; required: boolean; description: string; enum?: string[] }[];
}) {
  if (params.length === 0) return null;
  return (
    <div>
      <h3 className="mb-1 font-semibold">{title}</h3>
      <ul className="space-y-0.5">
        {params.map((param) => (
          <li key={param.name} className="text-xs">
            <span className="font-mono">{param.name}</span>{" "}
            <span className="text-muted-foreground">
              ({param.type}
              {param.required ? ", required" : ""}) {param.description}
              {param.enum ? ` — ${param.enum.join(", ")}` : ""}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CatalogTableRow({
  row,
  onOpenDoc,
}: {
  row: CatalogRow;
  onOpenDoc: (slug: string) => void;
}) {
  return (
    <TableRow>
      <TableCell className="font-mono text-xs text-muted-foreground">
        {row.doc?.method ?? "GET"}
      </TableCell>
      <TableCell>
        {/* An undocumented Endpoint still has to be identifiable, so the raw FeatureUrl
            path is the row's identity and the title is the decoration — not the reverse. */}
        <div className="font-mono text-sm">{row.path || "—"}</div>
        {row.doc ? (
          <div className="text-xs text-muted-foreground">
            {row.doc.title}
            {row.doc.summary ? ` — ${row.doc.summary}` : ""}
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">
            {row.description ?? "No description in the catalog"}
          </div>
        )}
      </TableCell>
      <TableCell>
        <div className="flex flex-wrap gap-1">
          {row.products.map((name) => (
            <Badge key={name} variant="outline">
              {name}
            </Badge>
          ))}
          {row.products.length === 0 && <span className="text-xs text-muted-foreground">—</span>}
        </div>
      </TableCell>
      <TableCell>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${COVERAGE_TONE[row.coverage]}`}
        >
          {row.coverage === "orphanDoc" && <FileWarning className="h-3 w-3" />}
          {CATALOG_COVERAGE_LABELS[row.coverage]}
        </span>
        {row.doc && !row.doc.visibleOnDocs && (
          <div className="mt-1 text-xs text-muted-foreground">Hidden from the docs site</div>
        )}
      </TableCell>
      <TableCell className="text-right">
        {row.doc ? (
          <div className="flex justify-end gap-1">
            <Button variant="ghost" size="sm" onClick={() => onOpenDoc(row.doc!.slug)}>
              View
            </Button>
            <Button variant="secondary" size="sm" asChild>
              <Link to={`/dashboard/b2b/catalog/${row.doc.slug}`}>Edit</Link>
            </Button>
          </div>
        ) : (
          // An endpoint nobody has documented: the editor opens with the path already
          // filled in, so writing its docs starts from what the catalog already knows.
          <Button variant="secondary" size="sm" asChild>
            <Link
              to={
                `/dashboard/b2b/catalog/new?path=${encodeURIComponent(row.path)}` +
                (row.featureUrlId !== null ? `&featureUrlId=${row.featureUrlId}` : "")
              }
            >
              Write docs
            </Link>
          </Button>
        )}
      </TableCell>
    </TableRow>
  );
}
